import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Package, Truck, Boxes, Users, Building2, CalendarClock, MapPin, Sparkles,
} from "lucide-react";

import OverviewTab from "@/pages/farm/OverviewTab";
import OrdersTab from "@/pages/farm/OrdersTab";
import OrderDrawer from "@/pages/farm/OrderDrawer";
import InventoryTab from "@/pages/farm/InventoryTab";
import StaffTab from "@/pages/farm/StaffTab";
import FinanceTab, { SuborderList } from "@/pages/farm/FinanceTab";
import ProfileTab from "@/pages/farm/ProfileTab";
import ShippingTab from "@/pages/farm/ShippingTab";
import DropsTab from "@/pages/farm/DropsTab";
import SocialKit from "@/components/SocialKit";

export default function VendorDashboard() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("overview");
  const [error, setError] = useState("");
  const [orderId, setOrderId] = useState(null);
  const [ordersRefresh, setOrdersRefresh] = useState(0);

  const reloadMe = () => api.get("/farm/me").then(r => setMe(r.data));
  const reloadOverview = () => api.get("/farm/overview").then(r => setOverview(r.data));

  useEffect(() => {
    if (!user) return;
    reloadMe().catch(e => setError(e.response?.data?.detail || "Failed to load farm portal"));
    reloadOverview().catch(() => {});
  }, [user]);

  const isPending = me?.vendor?.vendor_status && me.vendor.vendor_status !== "approved";

  const tabs = useMemo(() => {
    // Inventory / product listing is admin-controlled: the platform pulls
    // proteins from the farms directly, uploads/creates the drops itself,
    // and the vendor dashboard is purely for FULFILLMENT visibility. See
    // product-strategy doc — farmers do NOT self-list or self-schedule drops.
    const base = [
      { id: "overview", label: "Overview", icon: LayoutDashboard },
      { id: "orders", label: "Orders", icon: Package },
      { id: "fulfillment", label: "Fulfillment", icon: Truck },
      { id: "marketplace", label: "Marketplace orders", icon: Package },
    ];
    if (me?.is_owner) {
      base.push({ id: "inventory", label: "Products", icon: Package });
      base.push({ id: "drops", label: "Drops", icon: CalendarClock });
      base.push({ id: "finance", label: "Finance", icon: LayoutDashboard });
      base.push({ id: "shipping", label: "Shipping", icon: MapPin });
      base.push({ id: "profile", label: "Storefront", icon: Building2 });
      base.push({ id: "staff", label: "Team", icon: Users });
    }
    base.push({ id: "social", label: "Social kit", icon: Sparkles });
    return base;
  }, [me]);

  if (!user) return <div className="p-10">Please sign in.</div>;
  if (error) return <div className="p-10 max-w-2xl mx-auto"><div className="bg-rose-50 border border-rose-200 p-4 rounded text-sm text-rose-800" data-testid="farm-error">{error}</div></div>;
  if (!me || !overview) return <div className="p-10" data-testid="vendor-loading">Loading farm portal…</div>;

  const canFulfill = me.can_update_fulfillment;
  const canEdit = me.can_modify_inventory;

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="vendor-dashboard">
      <p className="overline">Farm partner portal</p>
      <div className="flex items-center gap-3 mt-3 flex-wrap">
        <h1 className="font-display text-5xl" data-testid="farm-name">{me.vendor.farm_name}</h1>
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[var(--ff-primary)]/10 text-[var(--ff-primary)] border border-[var(--ff-primary)]/20" data-testid="farm-role">
          {me.role === "farm_staff" ? "Staff" : "Owner"}
        </span>
      </div>
      <p className="text-sm text-[var(--ff-text-muted)] mt-1">{me.vendor.location} · {me.vendor.approved ? "Approved partner" : (me.vendor.vendor_status === "pending_invite" ? "Complete your profile to go live" : "Pending approval")}</p>

      <div className="mt-8 border-b border-[var(--ff-border)] overflow-x-auto" data-testid="farm-tabs">
        <div className="flex gap-1 min-w-max">
          {tabs.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            const highlight = t.id === "profile" && isPending;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-all ${active ? "border-[var(--ff-primary)] text-[var(--ff-primary)]" : "border-transparent text-[var(--ff-text-secondary)] hover:text-[var(--ff-primary)]"}`}>
                <Icon size={16}/> {t.label}
                {highlight && <span className="ml-1 w-2 h-2 rounded-full bg-amber-500 inline-block" data-testid="profile-dot"/>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-8">
        {tab === "overview" && <OverviewTab me={me} overview={overview}/>}
        {(tab === "orders" || tab === "fulfillment") && <OrdersTab canFulfill={canFulfill} onOpenOrder={setOrderId} refreshToken={ordersRefresh}/>}
        {tab === "inventory" && <InventoryTab canEdit={canEdit}/>}
        {tab === "drops" && me.is_owner && <DropsTab approved={me.vendor.vendor_status === "approved"}/>}
        {tab === "marketplace" && <SuborderList canFulfill={canFulfill}/>}
        {tab === "finance" && me.is_owner && <FinanceTab/>}
        {tab === "shipping" && me.is_owner && <ShippingTab/>}
        {tab === "profile" && me.is_owner && <ProfileTab me={me} onSaved={() => reloadMe()}/>}
        {tab === "staff" && me.is_owner && <StaffTab/>}
        {tab === "social" && <SocialKit/>}
      </div>

      {orderId && <OrderDrawer orderId={orderId} onClose={() => setOrderId(null)} canFulfill={canFulfill}
        onChanged={() => { setOrdersRefresh(n => n + 1); reloadOverview(); }}/>}
    </div>
  );
}
